import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getDashboardOverview, MEDDPICC_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, Users, RotateCcw } from "lucide-react";
import { LeaderboardCard } from "@/components/dashboard/LeaderboardCard";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";

const COACH_COLORS = {
  hint: "#E0A800", redirect: "#E01C4A", reinforcement: "#4ECDC4",
  reflection: "#6B3868", micro_summary: "#7D8CA3",
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selectedRep, setSelectedRep] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboardOverview()
      .then((res) => {
        setData(res.data);
        setSelectedRep(res.data.reps[0] || null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const gapData = data
    ? Object.entries(data.meddpicc_gaps).map(([k, v]) => ({
        dim: k, name: MEDDPICC_LABELS[k]?.name || k, value: Math.round(v * 100), fill: MEDDPICC_LABELS[k]?.color,
      }))
    : [];

  const coachData = data
    ? Object.entries(data.coaching_patterns).map(([k, v]) => ({
        type: k.replace(/_/g, " "), count: v, fill: COACH_COLORS[k] || "#7D8CA3",
      }))
    : [];

  return (
    <div className="min-h-screen bg-slate-50/50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50 accent-border-top">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Deal Velocity · Akamai</h1>
              <p className="text-xs text-slate-400">Manager Dashboard</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/profile">
              <Button variant="outline" size="sm" className="border-slate-200" data-testid="nav-profile-btn">
                My Profile
              </Button>
            </Link>
            <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => navigate("/")}
              data-testid="dashboard-home-btn">
              New Session
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {data?.by_role && Object.keys(data.by_role).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dash-role-breakdown">
            {Object.entries(data.by_role).map(([rid, v]) => (
              <Card key={rid} className="border-slate-200" style={{ borderTop: "3px solid #FF9933" }} data-testid={`dash-role-card-${rid}`}>
                <CardContent className="pt-5 pb-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">{rid === "sdr" ? "SDR Edition" : rid === "sales" ? "Sales Edition" : rid}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{v.sessions} <span className="text-sm font-normal text-slate-400">sessions</span></p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-slate-500">Avg trust <span className="font-semibold text-slate-900">{v.avg_trust}</span></p>
                    <p className="text-slate-500">Avg DVI <span className="font-semibold text-slate-900">{v.avg_dvi ?? "—"}</span></p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        <LeaderboardCard />

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Registered Reps</p>
              <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="dash-total-reps">{data?.total_reps ?? 0}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Total Sessions</p>
              <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="dash-total-sessions">{data?.total_sessions ?? 0}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Coach Interventions</p>
              <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="dash-total-coaching">
                {coachData.reduce((a, c) => a + c.count, 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Rep table */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> Team Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!data?.reps?.length ? (
              <p className="text-sm text-slate-400 py-8 text-center">No sessions recorded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="rep-table">
                  <thead>
                    <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-100">
                      <th className="py-2 pr-4">Rep</th>
                      <th className="py-2 pr-4">Sessions</th>
                      <th className="py-2 pr-4">Avg Trust</th>
                      <th className="py-2 pr-4">Avg DVI</th>
                      <th className="py-2 pr-4">Weakest Dimension</th>
                      <th className="py-2 pr-4">Coaching</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reps.map((r) => {
                      const dims = Object.entries(r.meddpicc_avg || {});
                      const weakest = dims.length ? dims.reduce((a, b) => (a[1] <= b[1] ? a : b)) : null;
                      const coachTotal = Object.values(r.coaching_patterns || {}).reduce((a, c) => a + c, 0);
                      return (
                        <tr key={r.rep_id}
                          className={`border-b border-slate-50 cursor-pointer hover:bg-slate-50 ${selectedRep?.rep_id === r.rep_id ? "bg-primary/5" : ""}`}
                          onClick={() => setSelectedRep(r)}
                          data-testid={`rep-row-${r.rep_id}`}>
                          <td className="py-3 pr-4">
                            <p className="font-medium text-slate-800">{r.name}</p>
                            {r.email && <p className="text-xs text-slate-400">{r.email}</p>}
                          </td>
                          <td className="py-3 pr-4">{r.completed_sessions}/{r.total_sessions}</td>
                          <td className="py-3 pr-4">{r.avg_trust}</td>
                          <td className="py-3 pr-4 font-semibold">{r.avg_dvi != null ? r.avg_dvi : "—"}</td>
                          <td className="py-3 pr-4">
                            {weakest && (
                              <Badge variant="outline" className="text-xs border-slate-200">
                                {MEDDPICC_LABELS[weakest[0]]?.name || weakest[0]} ({Math.round(weakest[1] * 100)}%)
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 pr-4">{coachTotal}</td>
                          <td className="py-3 text-right">
                            <Button size="sm" variant="outline" className="border-slate-200"
                              onClick={(e) => { e.stopPropagation(); navigate(`/replay/${r.latest_session_id}`); }}
                              data-testid={`rep-replay-${r.rep_id}`}>
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Trust Curve {selectedRep ? `— ${selectedRep.name}` : ""}
              </CardTitle>
              <p className="text-xs text-slate-400">Latest session, turn by turn</p>
            </CardHeader>
            <CardContent>
              {selectedRep?.trust_curve?.length ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={selectedRep.trust_curve}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="turn" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="trust" stroke="#0F3559" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-400 py-12 text-center">No trust events for this rep yet.</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">MEDDPICC Gaps (Team Avg)</CardTitle>
              <p className="text-xs text-slate-400">Lower bars = biggest coaching opportunities</p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={gapData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="dim" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip formatter={(v, n, p) => [`${v}%`, p.payload.name]} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {gapData.map((entry) => <Cell key={entry.dim} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Coaching Patterns</CardTitle>
              <p className="text-xs text-slate-400">Intervention types across all sessions</p>
            </CardHeader>
            <CardContent>
              {coachData.length ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={coachData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="type" tick={{ fontSize: 11 }} width={90} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {coachData.map((entry) => <Cell key={entry.type} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-slate-400 py-12 text-center">No coaching data yet.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
